import React, { useState, useEffect } from "react";
import CSVReader from "react-csv-reader";
import { Modal, Row, Col } from "react-bootstrap";
import Select from "react-select";

import {
  getDate,
  createMappedArray,
  handleColumnMapping,
  handleFileLoaded,
  beautifyStringForImport,
  validateDateForExport,
} from "../../../shared/utility";
import Button from "../../../shared/components/Buttons/Button";
import { UploadCsvIcon } from "../../../assets/icons/iconsProvider";
import {
  buttonNameConstants,
  buttonTypeConstants,
  COL_NAMES,
  customerConstants,
  EMPTY_STRING,
} from "../../../constants";
import { importSelectFieldDropdownStyle } from "../../../assets/scss/style";
import {
  emailRegex,
  cleanImportPhoneRegex,
  importPhoneRegex,
} from "../../../shared/validationSchema/validationSchema";

const ImportCustomer = (props) => {
  const {
    currentStore,
    duplicateCustomers,
    bulkUploadCustomers,
    importCustomerModal,
    emptyDuplicateRecords,
    toggleImportCustomerModal,
  } = props;

  //-------useState
  const [csvData, setCsvData] = useState("");
  const [columnMapping, setColumnMapping] = useState({});
  const predefinedColumns = [
    COL_NAMES.CUSTOMER_ID,
    COL_NAMES.FIRST_NAME,
    COL_NAMES.LAST_NAME,
    COL_NAMES.EMAIL,
    COL_NAMES.PHONE_NUMBER,
    COL_NAMES.ID_TYPE,
    COL_NAMES.ID_NUMBER,
    COL_NAMES.DATE_OF_BIRTH,
    COL_NAMES.EXPIRATION_DATE,
    COL_NAMES.STREET_1,
    COL_NAMES.STREET_2,
    COL_NAMES.CITY,
    COL_NAMES.STATE,
    COL_NAMES.ZIP,
    COL_NAMES.NOTES,
    COL_NAMES.CUSTOMER_CREDIT,
  ];
  const requiredColumns = [COL_NAMES.FIRST_NAME, COL_NAMES.LAST_NAME];

  //-------handle Hide Modal
  const handleHideModal = () => {
    toggleImportCustomerModal();
    setTimeout(() => {
      emptyDuplicateRecords();
      setCsvData("");
      setColumnMapping({});
    }, 500);
  };

  // validate csv data
  const validateCsvData = (customers) => {
    let validData = [];
    let invalidData = [];

    // for each row email or phone must be provided
    let errors = [];
    customers.forEach((rowData) => {
      errors = [];
      // email or phone is required
      // if (
      //   (rowData[COL_NAMES.EMAIL] === EMPTY_STRING ||
      //     rowData[COL_NAMES.EMAIL] === " ") &&
      //   (rowData[COL_NAMES.PHONE_NUMBER] === EMPTY_STRING ||
      //     rowData[COL_NAMES.PHONE_NUMBER] === " ")
      // ) {
      //   errors.push("Email & Phone both cannot be empty.");
      // }
      // required field is empty (first name)
      if (
        rowData[COL_NAMES.FIRST_NAME] === EMPTY_STRING ||
        rowData[COL_NAMES.FIRST_NAME] === " "
      ) {
        errors.push("Invalid/missing First Name value.");
      }
      // required field is empty (last name)
      if (
        rowData[COL_NAMES.LAST_NAME] === EMPTY_STRING ||
        rowData[COL_NAMES.LAST_NAME] === " "
      ) {
        errors.push("Invalid/missing Last Name value.");
      }
      // required field is invalid (email)
      if (
        rowData[COL_NAMES.EMAIL] &&
        rowData[COL_NAMES.EMAIL] !== EMPTY_STRING &&
        rowData[COL_NAMES.EMAIL] !== " " &&
        !emailRegex.test(rowData[COL_NAMES.EMAIL])
      ) {
        errors.push("Invalid/missing Email value.");
      }
      // required field is invalid (phone number)
      if (
        rowData[COL_NAMES.PHONE_NUMBER] &&
        rowData[COL_NAMES.PHONE_NUMBER] !== EMPTY_STRING &&
        rowData[COL_NAMES.PHONE_NUMBER] !== " " &&
        !importPhoneRegex.test(rowData[COL_NAMES.PHONE_NUMBER])
      ) {
        errors.push("Invalid/missing Phone Number value.");
      }
      // driver's liscense id type is invalid
      if (
        rowData[COL_NAMES.ID_TYPE] &&
        rowData[COL_NAMES.ID_TYPE] !== EMPTY_STRING &&
        rowData[COL_NAMES.ID_TYPE] !== customerConstants.ID_TYPES[0].value &&
        rowData[COL_NAMES.ID_TYPE] !== customerConstants.ID_TYPES[1].value
      ) {
        errors.push(
          `Invalid/missing Id Type value. (Valid values: ${customerConstants.ID_TYPES[0].value}, ${customerConstants.ID_TYPES[1].value})`
        );
      }
      // Expiration Date is invalid
      const rowDataExpirationDate = rowData[COL_NAMES.EXPIRATION_DATE];
      if (!validateDateForExport(rowDataExpirationDate)) {
        errors.push(
          "Invalid Expiration Date. Use MM-DD-YYYY or MM/DD/YYYY, or leave the field empty."
        );
      }
      // Date of Birth is invalid
      const rowDataDateOfBirth = rowData[COL_NAMES.DATE_OF_BIRTH];
      if (!validateDateForExport(rowDataDateOfBirth)) {
        errors.push(
          "Invalid Date of Birth. Use MM-DD-YYYY or MM/DD/YYYY, or leave the field empty."
        );
      }

      // adding rejection reasons if any
      if (errors.length) {
        invalidData.push({
          ...rowData,
          "Rejection Reason": beautifyStringForImport(errors.join(" | ")),
        });
      } else {
        validData.push(rowData);
      }
    });
    customers = validData;
    validData = [];

    return {
      validData: customers,
      invalidData,
    };
  };

  // ------- format phone number to required format
  const formatPhoneNumber = (number) => {
    // Remove all non-digit characters
    const digits = number.replace(/\D/g, "");

    // Format the phone number
    if (digits.length <= 3) {
      return `(${digits}`;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  // clean csv data
  const cleanCsvData = (customers) => {
    // converting customer credit to 0 if not provided
    customers.forEach((rowData) => {
      if (
        !rowData[COL_NAMES.CUSTOMER_CREDIT] ||
        isNaN(rowData[COL_NAMES.CUSTOMER_CREDIT])
      ) {
        rowData[COL_NAMES.CUSTOMER_CREDIT] = 0;
      }
      rowData[COL_NAMES.PHONE_NUMBER] = rowData[COL_NAMES.PHONE_NUMBER]
        ? formatPhoneNumber(
            String(rowData[COL_NAMES.PHONE_NUMBER]).replace(
              cleanImportPhoneRegex,
              ""
            )
          )
        : EMPTY_STRING;
    });
    // no return as it is pass by reference and updating the original array
  };

  const handleStartImport = () => {
    // either email or phone must be selected
    // if (
    //   !columnMapping.hasOwnProperty(COL_NAMES.EMAIL) &&
    //   !columnMapping.hasOwnProperty(COL_NAMES.PHONE_NUMBER)
    // ) {
    //   customToast("Email or phone number is required.", toastType.ERROR);
    //   return;
    // }

    const arrayAfterMapping = createMappedArray(
      csvData,
      predefinedColumns,
      columnMapping
    );
    // validate and clean csv data
    let validateCsvDataRes = validateCsvData(arrayAfterMapping);
    cleanCsvData(validateCsvDataRes.validData);

    //-------api call to uploade the csv records
    validateCsvDataRes.validData = validateCsvDataRes.validData.map(
      (customer) => ({
        companyCustomerId:
          customer[COL_NAMES.CUSTOMER_ID] &&
          !isNaN(customer[COL_NAMES.CUSTOMER_ID])
            ? customer[COL_NAMES.CUSTOMER_ID]
            : 0, // if 0 backend will generate a new id, otherwise given id will be used
        firstName: customer[COL_NAMES.FIRST_NAME],
        lastName: customer[COL_NAMES.LAST_NAME],
        email: customer[COL_NAMES.EMAIL] || EMPTY_STRING,
        mobile: customer[COL_NAMES.PHONE_NUMBER]
          ? customer[COL_NAMES.PHONE_NUMBER]
          : EMPTY_STRING,
        driverLicenseIdType:
          customer[COL_NAMES.ID_TYPE] || customerConstants.ID_TYPES[0].value,
        driverLicense: customer[COL_NAMES.ID_NUMBER] || EMPTY_STRING,
        dateOfBirth: getDate(customer[COL_NAMES.DATE_OF_BIRTH], false, false),
        expirationDate: getDate(
          customer[COL_NAMES.EXPIRATION_DATE],
          false,
          false
        ),
        line1: customer[COL_NAMES.STREET_1] || EMPTY_STRING,
        line2: customer[COL_NAMES.STREET_2] || EMPTY_STRING,
        city: customer[COL_NAMES.CITY] || EMPTY_STRING,
        state: customer[COL_NAMES.STATE] || EMPTY_STRING,
        zip: customer[COL_NAMES.ZIP] || EMPTY_STRING,
        note: customer[COL_NAMES.NOTES] || EMPTY_STRING,
        currentBalance: customer[COL_NAMES.CUSTOMER_CREDIT] || 0,
        isSmsAlert: false,
        isEmailAlert: false,
        productNotification: [],
        store: {
          id: currentStore?.id,
          name: currentStore?.storeName,
        },
        companyId: currentStore?.companyId,
      })
    );
    bulkUploadCustomers(currentStore?.companyId, currentStore?.id, {
      validateCsvDataRes,
      handleHideModal,
    });
  };

  //-------duplicate records find from backend
  useEffect(() => {
    if (duplicateCustomers?.length > 0) {
      toggleImportCustomerModal();
    }
    // eslint-disable-next-line
  }, [duplicateCustomers]);

  return (
    <Modal
      show={importCustomerModal}
      size={csvData ? "lg" : "md"}
      onHide={handleHideModal}
      animation={true}
      centered
      backdrop="static"
      className="add-inventory-modal"
    >
      <Modal.Header closeButton className="add-inventory-modal-header">
        <Modal.Title>
          <span className="add-inventory-modal-header-name">
            Import Customers
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="add-inventory-modal-body ">
        {csvData ? (
          <>
            <p className="p-0 m-0">* = Required Field</p>
            <Row className="m-0 px-0">
              <div className="column-mapping-table-wrapper container-scrollbar">
                <Row className="m-0 py-3 border-bottom px-0 import-inventory-column-header-wrapper">
                  <Col md={6} className="d-flex align-items-center">
                    <span className="import-inventory-columns-heading">
                      Inventory Columns
                    </span>
                  </Col>
                  <Col md={6} className="d-flex align-items-center">
                    <span className="import-inventory-columns-heading">
                      Your Column Header
                    </span>
                  </Col>
                </Row>
                {predefinedColumns.map((predefinedColumn, index) => (
                  <Row key={index} className="m-0 py-2 border-bottom px-0">
                    <Col md={6} className="d-flex align-items-center">
                      <span className="predefinded-column-label">
                        {predefinedColumn}
                        {requiredColumns.includes(predefinedColumn) && "*"}
                      </span>
                    </Col>
                    <Col
                      md={6}
                      className="d-flex justify-content-start align-items-center"
                    >
                      <Select
                        options={Object.keys(csvData[0]).map((csvColumn) => ({
                          value: csvColumn,
                          label: csvColumn,
                        }))}
                        value={{
                          value: columnMapping[predefinedColumn] || "",
                          label:
                            columnMapping[predefinedColumn] || "Select Column",
                        }}
                        onChange={(selectedOption) =>
                          handleColumnMapping(
                            predefinedColumn,
                            selectedOption.value,
                            setColumnMapping
                          )
                        }
                        styles={importSelectFieldDropdownStyle}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        className="w-100"
                      />
                    </Col>
                  </Row>
                ))}
              </div>
              <Col
                md={12}
                className="mt-4 d-flex justify-content-end align-items-center gap-3 px-0"
              >
                <Button
                  type="button"
                  label={buttonNameConstants.BACK}
                  buttonType={buttonTypeConstants.GHOST_BUTTON}
                  handleClick={() => setCsvData("")}
                />
                <Button
                  type="button"
                  label={buttonNameConstants.START_IMPORT}
                  handleClick={() => handleStartImport()}
                  isDisabled={requiredColumns.find((col) => {
                    return !columnMapping.hasOwnProperty(col);
                  })}
                />
              </Col>
            </Row>
          </>
        ) : (
          <Row className="m-0">
            <Col
              md={12}
              className="d-flex justify-content-center align-items-center"
            >
              <label
                className="d-flex flex-column justify-content-center align-items-center gap-2 upload-csv-wrapper"
                style={{ height: "fit-content" }}
              >
                <UploadCsvIcon />
                Click or drag file to this area to upload
                <CSVReader
                  inputId="CSVReader"
                  key={Date.now()}
                  inputStyle={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    cursor: "pointer",
                    opacity: 0,
                  }}
                  onFileLoaded={(data, fileInfo) =>
                    handleFileLoaded(
                      data,
                      fileInfo,
                      predefinedColumns,
                      setCsvData,
                      setColumnMapping
                    )
                  }
                  parserOptions={{ header: true, skipEmptyLines: true }}
                  accept=".csv"
                />
              </label>
            </Col>
            <Col md={7}></Col>
            <Col
              md={5}
              className="mt-3 d-flex justify-content-end align-items-center"
            >
              <Button
                type="button"
                label={buttonNameConstants.CANCEL}
                buttonType={buttonTypeConstants.GHOST_BUTTON}
                handleClick={() => handleHideModal()}
              />
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ImportCustomer;
